'use client'

import React, { useEffect, useState } from 'react'
import {
  Alert,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
} from '@repo/ui/components'
import {
  Button,
  Typography,
  Row,
  Col,
  Icon,
  Input,
  Slider,
} from '@repo/ui/components'
import { useErrorHandler, useTranslate } from '@repo/shared/hooks'
import { CommissionVisualization } from '@repo/shared/components'
import Image from 'next/image'
import type {
  PropertySelectionModalProps,
  ModalStep,
  StepFormData,
  UploadedImage,
} from './dashboard.property-selection-modal.types'
import { globalConfig } from '@repo/shared/utils'
import {
  AgentPostPhoto,
  useAgentAddPost,
  useAgentCreateProjectLink,
  useAgentUploadPostPhoto,
  useAgentEditPost,
  useAgentListPosts,
  useResetProjects,
  useAgentAuthDetails,
  useResetPosts,
  useGetAgentReferralCodes,
} from '@repo/services'
import { ImageUploader } from '@/components/image-uploader'
import { PropertyDetails } from './property-details'

export const PropertySelectionModal = ({
  open,
  onOpenChange,
  property,
  viewType,
  post: _post, // eslint-disable-line @typescript-eslint/no-unused-vars
}: PropertySelectionModalProps) => {
  const { errorHandler } = useErrorHandler()
  const { data: authDetails } = useAgentAuthDetails()
  const { data } = useGetAgentReferralCodes()
  const referralCode = data?.data?.codes?.[0]?.code
  const { t } = useTranslate()

  const { resetAgentProjects, resetAgentListAvailableProjects } =
    useResetProjects()

  const { resetAgentPosts } = useResetPosts()

  const [currentViewType, setCurrentViewType] = useState<'details' | 'choose'>(
    viewType,
  )

  const [currentStep, setCurrentStep] = useState<ModalStep>(
    viewType === 'details' ? 5 : 1,
  )
  const { data: postsData } = useAgentListPosts(
    {
      skip: 0,
      limit: 2,
      authorID: authDetails?.data.id,
      projectID: property.id,
    },
    !!authDetails?.data.id,
  )

  const post = (postsData?.pages.flatMap((page) => page.data?.posts || []) ||
    [])[0]

  const [pendingImages, setPendingImages] = useState<UploadedImage[]>([])
  const [isUploadingImages, setIsUploadingImages] = useState(false)

  const [stepFormData, setStepFormData] = useState<StepFormData>({
    commissionDiscount: 0,
    text: '',
    pros: [''],
    cons: [''],
    initialImages: [],
  })

  useEffect(() => {
    if (post) {
      const commissionDiscount = 0
      const initialImages =
        post?.photos?.map((photo: AgentPostPhoto, idx: number) => ({
          id: photo.id || 0,
          url: `${globalConfig.baseImageUrl}${photo.smallURL}`,
          name: `image-${idx}`,
          size: 0,
          file: null,
          status: 'success' as const, // Ensure existing images have success status
        })) || []
      setStepFormData({
        commissionDiscount: commissionDiscount || 0,
        text: post?.body || '',
        pros: post?.pros || [''],
        cons: post?.cons || [''],
        initialImages: initialImages,
      })
    }
  }, [post])

  const {
    data: addPostData,
    mutate: addPostMutation,
    isPending: isAddPostPending,
  } = useAgentAddPost({
    invalidate: false,
  })
  const postId = addPostData?.data.post?.id || post?.id

  const { mutate: editPostMutation, isPending: isEditPostPending } =
    useAgentEditPost({
      invalidate: false,
    })
  const {
    mutate: createProjectLinkMutation,
    isPending: isCreateProjectLinkPending,
  } = useAgentCreateProjectLink({
    invalidate: false,
  })

  const {
    mutate: uploadPostPhotoMutation,
    isPending: isUploadPostPhotoPending,
  } = useAgentUploadPostPhoto()

  const handleClose = (open: boolean) => {
    if (!open) {
      if (
        currentStep === 2 ||
        currentStep === 3 ||
        currentStep === 4 ||
        currentStep === 5
      ) {
        resetAgentProjects()
        resetAgentListAvailableProjects()
      }
    }
    onOpenChange(open)
  }
  const handleImageUpload = (
    file: File,
    postID: number,
    callback: (success: boolean) => void,
  ) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('postID', postID.toString())

    uploadPostPhotoMutation(formData, {
      onSuccess: () => {
        callback(true)
      },
      onError: () => {
        callback(false)
      },
    })
  }
  const handleDeleteImage = (fileId: number) => {
    // TODO: Implement image deletion
    // resetAgentPosts()
    // For now, just log the fileId to avoid unused parameter warning
    void fileId
  }

  const ensureExistingFilesSuccess = (files: UploadedImage[]) => {
    return files.map((file) =>
      file.file === null ? { ...file, status: 'success' } : file,
    )
  }

  const uploadPendingImages = async (postID: number) => {
    // Ensure existing images (file === null) have success status
    const updatedPendingImages = ensureExistingFilesSuccess(pendingImages)

    const pendingImagesToUpload = updatedPendingImages.filter(
      (img) => img.status === 'pending',
    )
    if (pendingImagesToUpload.length === 0) return true

    setIsUploadingImages(true)
    let hasError = false

    for (const image of pendingImagesToUpload) {
      if (!image.file) continue

      try {
        await new Promise<void>((resolve, reject) => {
          handleImageUpload(image.file!, postID, (success) => {
            if (success) {
              setPendingImages((prev) =>
                prev.map((img) =>
                  img.id === image.id ? { ...img, status: 'success' } : img,
                ),
              )
              resolve()
            } else {
              setPendingImages((prev) =>
                prev.map((img) =>
                  img.id === image.id
                    ? { ...img, status: 'error', error: 'Upload failed' }
                    : img,
                ),
              )
              hasError = true
              reject(new Error('Upload failed'))
            }
          })
        })
      } catch (error) {
        errorHandler(error, {
          type: 'both',
        })

        hasError = true
      }
    }

    setIsUploadingImages(false)
    return !hasError
  }

  const handleCreateProjectLink = async () => {
    createProjectLinkMutation(
      {
        token: property.token || '',
        data: {
          token: property.token || '',
          discount: {
            value: stepFormData.commissionDiscount,
          },
        },
      },
      {
        onSuccess: () => {
          handleNext()
        },
      },
    )
  }

  const handleAddPost = () => {
    addPostMutation(
      {
        token: property.token || '',
        data: {
          text: stepFormData.text,
          pros:
            stepFormData.pros.length === 1 && stepFormData.pros[0] === ''
              ? undefined
              : stepFormData.pros,
          cons:
            stepFormData.cons.length === 1 && stepFormData.cons[0] === ''
              ? undefined
              : stepFormData.cons,
        },
      },
      {
        onSuccess: async (data) => {
          const uploadImagesSuccess = await uploadPendingImages(
            data.data.post?.id || 0,
          )

          if (uploadImagesSuccess) {
            resetAgentPosts()
            setCurrentViewType('details')
            handleNext()
          }
        },
      },
    )
  }

  const handleEditPost = () => {
    editPostMutation(
      {
        token: property.token || '',
        postId: postId || 0,
        data: {
          postID: postId || 0,
          body: stepFormData.text,
          pros:
            stepFormData.pros.length === 1 && stepFormData.pros[0] === ''
              ? undefined
              : stepFormData.pros,
          cons:
            stepFormData.cons.length === 1 && stepFormData.cons[0] === ''
              ? undefined
              : stepFormData.cons,
        },
      },
      {
        onSuccess: async (data) => {
          const uploadImagesSuccess = await uploadPendingImages(
            data.data.post?.id || 0,
          )

          if (uploadImagesSuccess) {
            resetAgentPosts()
            setCurrentViewType('details')
            handleNext()
          }
        },
      },
    )
  }

  const handleOpinion = () => {
    if (currentViewType === 'details') {
      handleEditPost()
    } else {
      handleAddPost()
    }
  }

  const handleNext = () => {
    setCurrentStep((prev) => (prev + 1) as ModalStep)
  }
  const handle2Next = () => {
    setCurrentStep((prev) => (prev + 2) as ModalStep)
  }

  const handleToEditPost = () => {
    setCurrentViewType('details')
    setCurrentStep(4)
  }
  const handleToAddPost = () => {
    setCurrentViewType('choose')
    setCurrentStep(4)
  }

  const handleToShare = () => {
    if (navigator.share) {
      navigator.share({
        text: t('dashboardAgent_projects_share_text', {
          name: property.displayName || '',
          url: `${globalConfig.originWebUrl}/rwa/${property.token}/aff/${referralCode}`,
        }),
      })
    } else {
      // alert('Sharing is not supported in this browser.')
    }
  }

  const addStrength = () => {
    setStepFormData({
      ...stepFormData,
      pros: [...stepFormData.pros, ''],
    })
  }

  const removeStrength = (index: number) => {
    setStepFormData({
      ...stepFormData,
      pros: stepFormData.pros.filter((_, i) => i !== index),
    })
  }

  const updateStrength = (index: number, value: string) => {
    const newPros = [...stepFormData.pros]
    newPros[index] = value
    setStepFormData({
      ...stepFormData,
      pros: newPros,
    })
  }

  const addWeakness = () => {
    setStepFormData({
      ...stepFormData,
      cons: [...stepFormData.cons, ''],
    })
  }

  const removeWeakness = (index: number) => {
    setStepFormData({
      ...stepFormData,
      cons: stepFormData.cons.filter((_, i) => i !== index),
    })
  }

  const updateWeakness = (index: number, value: string) => {
    const newCons = [...stepFormData.cons]
    newCons[index] = value
    setStepFormData({
      ...stepFormData,
      cons: newCons,
    })
  }

  const renderStep1 = () => (
    <Col className='gap-lg'>
      <Typography size='sm' color='text-neutral-surface-100'>
        {t('dashboardAgent_commission_discount_info')}
      </Typography>

      <Typography size='sm' color='text-neutral-surface-100'>
        {t('dashboardAgent_commission_discount_info_2')}
      </Typography>

      <CommissionVisualization
        agentShare={50 - stepFormData.commissionDiscount}
        customerDiscount={stepFormData.commissionDiscount}
        agentTitle={t('dashboardAgent_select_your_commission')}
        customerTitle={t('dashboardAgent_select_commission_discount')}
        ownerTitle={t('dashboardAgent_select_owner_commission')}
        ownerPercentage={50}
        className='mt-lg'
      />

      <Col className='gap-xs'>
        <Row className='justify-between items-center'>
          <Col className='justify-between items-start gap-xs'>
            <Typography size='sm'>
              {t('dashboardAgent_select_commission_discount')}
            </Typography>
            <Typography size='sm' weight='bold'>
              {stepFormData.commissionDiscount} {t('dashboardAgent_percent')}
            </Typography>
          </Col>
          <Col className='justify-between items-end gap-xs'>
            <Typography size='sm'>
              {t('dashboardAgent_select_your_commission')}
            </Typography>
            <Typography size='sm' weight='bold'>
              {50 - stepFormData.commissionDiscount}{' '}
              {t('dashboardAgent_percent')}
            </Typography>
          </Col>
        </Row>
        <Slider
          value={[stepFormData.commissionDiscount]}
          onValueChange={(values) =>
            setStepFormData({
              ...stepFormData,
              commissionDiscount: values[0] || 0,
            })
          }
          min={0}
          max={50}
          step={1}
          color='base'
          showTooltip={false}
        />
      </Col>

      <Alert
        caption={t('dashboardAgent_commission_discount_info_alert')}
        color='alert'
        icon={<Icon name='AlertTriangle' />}
      />

      <Button
        variant='fill'
        color='base'
        size='md'
        width='full'
        endIcon={<Icon name='ArrowLeft' size='sm' />}
        onClick={handleCreateProjectLink}
      >
        {t(
          stepFormData.commissionDiscount === 0
            ? 'dashboardAgent_continue_without_discount'
            : 'dashboardAgent_continue',
        )}
      </Button>
    </Col>
  )

  const renderStep2 = () => (
    <Col className='gap-lg  items-center'>
      <Image
        src='/images/money.svg'
        alt=''
        width={80}
        height={80}
        className='mx-auto'
      />
      <Typography size='md' color='text-positive-surface-100'>
        {t('dashboardAgent_set_commission_discount_success_title')}
      </Typography>
      <Typography size='md'>
        {t('dashboardAgent_set_commission_discount_success_description')}
      </Typography>
      <Button
        variant='transparent'
        color='neutral'
        size='md'
        width='full'
        onClick={handleNext}
      >
        {t(
          'dashboardAgent_set_commission_discount_success_button_cancel_opinion',
        )}
      </Button>
      <Button
        variant='fill'
        color='base'
        size='md'
        width='full'
        onClick={handle2Next}
        endIcon={<Icon name='ArrowLeft' size='sm' />}
      >
        {t(
          'dashboardAgent_set_commission_discount_success_button_confirm_add_opinion',
        )}
      </Button>
    </Col>
  )

  const renderStep3 = () => (
    <Col className='gap-lg'>
      <Image
        src='/images/money.svg'
        alt=''
        width={80}
        height={80}
        className='mx-auto'
      />
      <Typography size='sm'>
        {t('dashboardAgent_add_expert_opinion_cancel_description')}
      </Typography>
      <Button
        variant='fill'
        color='negative'
        size='md'
        width='full'
        onClick={handle2Next}
      >
        {t('dashboardAgent_add_expert_opinion_cancel_button_cancel_opinion')}
      </Button>
      <Button
        variant='ghost'
        color='neutral'
        size='md'
        width='full'
        onClick={handleNext}
      >
        {t(
          'dashboardAgent_add_expert_opinion_cancel_button_confirm_add_opinion',
        )}
      </Button>
    </Col>
  )

  const renderStep4 = () => (
    <Col className='gap-lg'>
      <Typography size='sm'>
        {t('dashboardAgent_expert_opinion_description')}
      </Typography>

      {/* Your Opinion About This Property */}
      <Col className='gap-xs'>
        <Typography size='xs' weight='bold'>
          {t('dashboardAgent_your_opinion_about_property')}
        </Typography>
        <Input
          as='textarea'
          placeholder={t('dashboardAgent_opinion_placeholder')}
          value={stepFormData.text}
          onChange={(e) =>
            setStepFormData({ ...stepFormData, text: e.target.value })
          }
          maxLength={1200}
          rows={4}
        />
        <Typography size='xs' className='text-start'>
          {t('dashboardAgent_max_characters')}
        </Typography>
      </Col>

      {/* pros and Advantages */}
      <Col className='gap-xs'>
        <Typography size='xs' weight='bold'>
          {t('dashboardAgent_strengths_and_advantages')}
        </Typography>
        {stepFormData.pros.map((strength, index) => (
          <Row key={index} className='gap-xs items-center'>
            <Input
              placeholder={t('dashboardAgent_strength_point')}
              value={strength}
              onChange={(e) => updateStrength(index, e.target.value)}
              className='flex-1'
            />
            <Button
              variant='transparent'
              size='sm'
              color='negative'
              onClick={() => removeStrength(index)}
              className='p-2xs'
            >
              <Icon name='TrashBin' size='xs' />
            </Button>
          </Row>
        ))}
        <Button
          variant='transparent'
          color='positive'
          size='sm'
          startIcon={<Icon name='Plus' size='xs' />}
          onClick={addStrength}
          className='w-fit'
        >
          {t('dashboardAgent_add_strength_point')}
        </Button>
      </Col>

      {/* cons and Disadvantages */}
      <Col className='gap-xs'>
        <Typography size='xs' weight='bold'>
          {t('dashboardAgent_weaknesses_and_disadvantages')}
        </Typography>
        {stepFormData.cons.map((weakness, index) => (
          <Row key={index} className='gap-xs items-center'>
            <Input
              placeholder={t('dashboardAgent_weakness_point')}
              value={weakness}
              onChange={(e) => updateWeakness(index, e.target.value)}
              className='flex-1'
            />
            <Button
              variant='transparent'
              size='sm'
              color='negative'
              onClick={() => removeWeakness(index)}
              className='p-2xs'
            >
              <Icon name='TrashBin' size='xs' />
            </Button>
          </Row>
        ))}
        <Button
          variant='transparent'
          color='positive'
          size='sm'
          startIcon={<Icon name='Plus' size='xs' />}
          onClick={addWeakness}
          className='w-fit'
        >
          {t('dashboardAgent_add_weakness_point')}
        </Button>
      </Col>
      <ImageUploader
        onUpload={handleImageUpload}
        // isLoading={isUploadingImages}
        accept='image/jpeg,image/png,image/webp'
        maxSize={5 * 1024 * 1024} // 5MB
        initialImages={
          ensureExistingFilesSuccess(
            stepFormData.initialImages,
          ) as UploadedImage[]
        }
        onDelete={handleDeleteImage}
        onImagesChange={setPendingImages}
        postId={postId || 0}
      />

      <Button
        variant='fill'
        color='base'
        size='md'
        width='full'
        endIcon={<Icon name='ArrowLeft' size='md' />}
        onClick={handleOpinion}
        loading={
          isUploadingImages ||
          isAddPostPending ||
          isEditPostPending ||
          isCreateProjectLinkPending ||
          isUploadPostPhotoPending
        }
      >
        {t('dashboardAgent_add_opinion_button')}
      </Button>
    </Col>
  )

  const renderStep5 = () => (
    <Col className='gap-md'>
      {/* Commission Discount */}
      {/* <Row className='gap-xs justify-between'>
        <Typography size='xs' color='text-neutral-surface-60'>
          {t('dashboardAgent_special_commission_discount')}
        </Typography>
        <Typography size='sm'>
          <NumberUnit
            value={stepFormData.commissionDiscount}
            unit={t('dashboardAgent_discount_percentage')}
            size='2xs'
            numberSize='sm'
          />
        </Typography>
      </Row> */}

      {/* Expert Opinion */}
      <Col className='gap-3xs'>
        {post && post.status === 'published' && (
          <Alert
            caption={t('dashboardAgent_add_expert_opinion_published_alert')}
            color='positive'
          />
        )}
        {((post && (post.status === 'in_review' || post.status === 'draft')) ||
          addPostData?.data.post?.id) && (
          <Alert
            caption={t('dashboardAgent_add_expert_opinion_under_review_alert')}
            color='alert'
            icon={<Icon name='AlertTriangle' size='sm' />}
          />
        )}
        {post && post.status === 'rejected' && (
          <Alert
            caption={t('dashboardAgent_add_expert_opinion_rejected_alert', {
              reason: post.audit?.comments?.[0] || '',
            })}
            color='negative'
          />
        )}

        {!postId && (
          <Col className='gap-md items-center'>
            <Image
              src='/images/money.svg'
              alt=''
              width={80}
              height={80}
              className='mx-auto'
            />
            <Typography size='sm' color='text-base-surface-100'>
              {t('dashboardAgent_add_expert_opinion_no_opinion_added')}
            </Typography>

            <Button
              variant='fill'
              color='base'
              size='sm'
              onClick={handleToAddPost}
              className='min-w-[180px]'
            >
              {t('dashboardAgent_add_expert_opinion_button')}
            </Button>
          </Col>
        )}

        {post && <PropertyDetails post={post} />}
      </Col>

      {/* Action Buttons */}
      <Col className='gap-md pt-md'>
        {!postId && (
          <Alert
            caption={t('dashboardAgent_add_expert_opinion_add_alert')}
            color='alert'
          />
        )}
        {postId && (
          <Button
            variant='ghost'
            color='base'
            size='md'
            width='full'
            startIcon={<Icon name='PencilWithALine' size='sm' />}
            onClick={handleToEditPost}
          >
            {t('dashboardAgent_go_back_and_edit')}
          </Button>
        )}

        <Button
          variant='fill'
          color='base'
          size='md'
          width='full'
          onClick={handleToShare}
          disabled={!(post && post.status === 'published')}
          loading={
            isAddPostPending ||
            isEditPostPending ||
            isCreateProjectLinkPending ||
            isUploadPostPhotoPending
          }
        >
          {t('dashboardAgent_share_button')}
        </Button>
      </Col>
    </Col>
  )

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return t('dashboardAgent_set_commission_discount')
      case 2:
        return null
      case 3:
        return null
      case 4:
        return t('dashboardAgent_add_expert_opinion')
      case 5:
        return property.displayName
      default:
        return ''
    }
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1()
      case 2:
        return renderStep2()
      case 3:
        return renderStep3()
      case 4:
        return renderStep4()
      case 5:
        return renderStep5()
      default:
        return null
    }
  }

  return (
    <Modal open={open} onOpenChange={handleClose}>
      <ModalContent className='sm:max-w-[550px] w-full '>
        <ModalHeader>
          <ModalTitle className='text-md font-bold'>
            {getStepTitle()}
          </ModalTitle>
        </ModalHeader>

        <div className='px-lg pb-lg max-h-[461px] overflow-y-auto'>
          {renderCurrentStep()}
        </div>
      </ModalContent>
    </Modal>
  )
}
